import { Target } from "lucide-react";
import Panel, { Empty } from "./Panel";
import { formatINR } from "../../../components/dashboard/shared";
import { cn } from "../../../utils/tailwindUtils";

const R = 30;
const C = 2 * Math.PI * R;

// A progress ring with a tick where the rep should be by today if they are
// to finish the month on target.
const Ring = ({ label, actual, target, pace, format = (v) => v }) => {
    const has = target !== null && target !== undefined && Number(target) > 0;
    const pct = has ? Math.min(100, (100 * Number(actual)) / Number(target)) : 0;
    const expected = has ? (Number(target) * pace) / 100 : 0;
    const ahead = has && Number(actual) >= expected;
    const gap = has ? Math.max(0, Math.ceil(expected - Number(actual))) : 0;
    const angle = (pace / 100) * 2 * Math.PI - Math.PI / 2;
    const tick = { x1: 40 + (R - 7) * Math.cos(angle), y1: 40 + (R - 7) * Math.sin(angle),
                   x2: 40 + (R + 7) * Math.cos(angle), y2: 40 + (R + 7) * Math.sin(angle) };
    return (
        <div className="flex flex-col items-center text-center min-w-0">
            <svg viewBox="0 0 80 80" className="w-20 h-20" role="img"
                 aria-label={`${label}: ${actual} of ${has ? target : "no target"}`}>
                <circle cx="40" cy="40" r={R} fill="none" strokeWidth="8"
                        className="stroke-gray-100 dark:stroke-gray-700" />
                {has && (
                    <circle cx="40" cy="40" r={R} fill="none" strokeWidth="8" strokeLinecap="round"
                            strokeDasharray={`${(pct / 100) * C} ${C}`} transform="rotate(-90 40 40)"
                            className={cn("transition-all duration-700",
                                ahead ? "stroke-emerald-500" : "stroke-brand-600")} />
                )}
                {has && <line {...tick} strokeWidth="2" className="stroke-gray-400 dark:stroke-gray-500" />}
                <text x="40" y="44" textAnchor="middle"
                      className="fill-gray-900 dark:fill-white text-[13px] font-bold">
                    {has ? `${Math.round(pct)}%` : "—"}
                </text>
            </svg>
            <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 mt-1">{label}</p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                {format(actual)}{has ? ` / ${format(target)}` : ""}
            </p>
            {has && (
                <p className={cn("text-[11px] font-medium mt-0.5",
                    ahead ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400")}>
                    {ahead ? "On track" : `Behind by ${label === "Revenue" ? formatINR(expected - actual) : gap}`}
                </p>
            )}
        </div>
    );
};

const TargetRings = ({ targets, loading }) => {
    const t = targets || {};
    return (
        <Panel title="This month's target" icon={Target} loading={loading}
               badge={t.days_left !== undefined && (
                   <span className="text-[11px] text-gray-500 dark:text-gray-400">{t.days_left} days left</span>
               )}>
            {!t.has_target ? (
                <Empty icon={Target} title="No target set for this month"
                       hint="Ask your manager to set one under Team Performance." />
            ) : (
                <>
                    <div className="grid grid-cols-3 gap-2">
                        <Ring label="Demos" actual={t.demos?.actual ?? 0} target={t.demos?.target} pace={t.pace_pct} />
                        <Ring label="Closures" actual={t.closures?.actual ?? 0} target={t.closures?.target} pace={t.pace_pct} />
                        <Ring label="Revenue" actual={t.revenue?.actual ?? 0} target={t.revenue?.target}
                              pace={t.pace_pct} format={formatINR} />
                    </div>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center mt-3">
                        The tick marks where you should be today ({Math.round(t.pace_pct)}% of the month gone).
                    </p>
                </>
            )}
        </Panel>
    );
};

export default TargetRings;
